import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Avatar, Button, Tabs, Tab,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, IconButton, Alert, Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Divider, Tooltip
} from '@mui/material';
import {
  ArrowBack, Edit, Add, Delete, Upload, Download, Person, Favorite, History,
  FamilyRestroom, HealthAndSafety, Description, Close, Warning, CheckCircle
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const bloodColors: Record<string, string> = {
  'A+': '#ef4444', 'B+': '#3b82f6', 'O+': '#10b981', 'AB+': '#8b5cf6',
  'A-': '#dc2626', 'B-': '#2563eb', 'O-': '#059669', 'AB-': '#7c3aed', 'Unknown': '#94a3b8'
};

const TabPanel = ({ value, index, children }: any) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const InfoItem = ({ label, value }: { label: string; value?: string }) => (
  <Box>
    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, display: 'block' }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 500, mt: 0.2 }}>{value || '—'}</Typography>
  </Box>
);

const SectionCard = ({ title, icon, action, children }: any) => (
  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, mb: 2.5 }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{title}</Typography>
        </Box>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const PatientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogData, setDialogData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/patients/${id}`);
      setPatient(res.data.data);
    } catch { toast.error('Failed to load patient'); navigate('/patients'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const openDialog = (type: string, data: any = {}) => {
    setDialogType(type);
    setDialogData(data);
    setError('');
    setEditOpen(true);
  };

  const saveSection = async (section: string, data: any) => {
    setSaving(true); setError('');
    try {
      let updateData: any = {};
      if (section === 'basic') {
        updateData = data;
      } else if (section === 'vitals') {
        updateData = { vitals: { ...data, lastUpdated: new Date() } };
      } else if (section === 'chronicConditions') {
        updateData = { chronicConditions: data.value ? data.value.split(',').map((s: string) => s.trim()).filter(Boolean) : [] };
      } else {
        // Array sections — push to array
        const current = patient[section] || [];
        updateData[section] = [...current, data];
      }
      await api.put(`/patients/${id}`, updateData);
      toast.success('Saved successfully');
      setEditOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const deleteFromArray = async (section: string, index: number) => {
    try {
      const current = [...(patient[section] || [])];
      current.splice(index, 1);
      await api.put(`/patients/${id}`, { [section]: current });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      fd.append('type', 'other');
      await api.post(`/patients/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const deleteDocument = async (docId: string) => {
    try {
      await api.delete(`/patients/${id}/documents/${docId}`);
      toast.success('Document deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (!patient) return null;

  const age = patient.dob ? `${new Date().getFullYear() - new Date(patient.dob).getFullYear()} years` : '—';
  const bmi = patient.vitals?.bmi;
  const bmiColor = bmi ? (bmi < 18.5 ? '#3b82f6' : bmi < 25 ? '#10b981' : bmi < 30 ? '#f59e0b' : '#ef4444') : '#94a3b8';

  const tabList = [
    { label: 'Overview', icon: <Person fontSize="small" /> },
    { label: 'Medical History', icon: <History fontSize="small" /> },
    { label: 'Family History', icon: <FamilyRestroom fontSize="small" /> },
    { label: 'Allergies', icon: <Warning fontSize="small" /> },
    { label: 'Insurance', icon: <HealthAndSafety fontSize="small" /> },
    { label: 'Documents', icon: <Description fontSize="small" /> },
  ];

  return (
    <Box>
      {/* Back + Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/patients')} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Patient Profile</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Registered on {new Date(patient.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Edit />} onClick={() => openDialog('basic', { name: patient.name, phone: patient.phone, email: patient.email, gender: patient.gender, bloodGroup: patient.bloodGroup, dob: patient.dob?.slice(0, 10) || '' })}
          sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0' }}>
          Edit Profile
        </Button>
      </Box>

      {/* Profile Header Card */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, mb: 2.5 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar sx={{ width: 80, height: 80, bgcolor: '#dbeafe', color: '#1d4ed8', fontSize: 28, fontWeight: 800 }}>
                {patient.name?.charAt(0).toUpperCase()}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{patient.name}</Typography>
                <Chip label={patient.patientId} size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 700, fontSize: 12 }} />
                {patient.bloodGroup && patient.bloodGroup !== 'Unknown' && (
                  <Chip label={patient.bloodGroup} size="small"
                    sx={{ bgcolor: `${bloodColors[patient.bloodGroup]}18`, color: bloodColors[patient.bloodGroup], fontWeight: 800, fontSize: 12, border: `1px solid ${bloodColors[patient.bloodGroup]}35` }} />
                )}
                <Chip label={patient.isActive ? 'Active' : 'Inactive'} size="small" color={patient.isActive ? 'success' : 'default'} />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <InfoItem label="Age" value={age} />
                <InfoItem label="Gender" value={patient.gender ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1) : undefined} />
                <InfoItem label="Phone" value={patient.phone} />
                <InfoItem label="Email" value={patient.email} />
                <InfoItem label="Occupation" value={patient.occupation} />
              </Box>
            </Grid>

            {/* Vitals Quick View */}
            {patient.vitals && (
              <Grid item>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Height', value: patient.vitals.height ? `${patient.vitals.height} cm` : null },
                    { label: 'Weight', value: patient.vitals.weight ? `${patient.vitals.weight} kg` : null },
                    { label: 'BMI', value: bmi ? `${bmi}` : null, color: bmiColor },
                    { label: 'BP', value: patient.vitals.bloodPressure },
                    { label: 'Pulse', value: patient.vitals.pulse ? `${patient.vitals.pulse} bpm` : null },
                  ].filter(v => v.value).map(v => (
                    <Box key={v.label} sx={{ textAlign: 'center', bgcolor: '#f8fafc', px: 2, py: 1, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{v.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: (v as any).color || '#0f172a' }}>{v.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
        <Box sx={{ borderBottom: '1px solid #f1f5f9', px: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 48 }, '& .Mui-selected': { color: '#1e40af' }, '& .MuiTabs-indicator': { bgcolor: '#1e40af' } }}>
            {tabList.map((t, i) => (
              <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>

          {/* TAB 0: Overview */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <SectionCard title="Personal Details" icon={<Person sx={{ color: '#3b82f6', fontSize: 20 }} />}
                  action={<Button size="small" startIcon={<Edit />} onClick={() => openDialog('basic', { name: patient.name, phone: patient.phone, email: patient.email, gender: patient.gender, bloodGroup: patient.bloodGroup, dob: patient.dob?.slice(0, 10) || '', occupation: patient.occupation, maritalStatus: patient.maritalStatus })} sx={{ textTransform: 'none', fontSize: 12 }}>Edit</Button>}>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Full Name', value: patient.name },
                      { label: 'Date of Birth', value: patient.dob ? new Date(patient.dob).toLocaleDateString('en-IN') : undefined },
                      { label: 'Age', value: age },
                      { label: 'Gender', value: patient.gender },
                      { label: 'Blood Group', value: patient.bloodGroup },
                      { label: 'Marital Status', value: patient.maritalStatus },
                      { label: 'Occupation', value: patient.occupation },
                      { label: 'Nationality', value: patient.nationality },
                    ].map(({ label, value }) => (
                      <Grid item xs={6} key={label}><InfoItem label={label} value={value} /></Grid>
                    ))}
                  </Grid>
                </SectionCard>

                <SectionCard title="Contact & Address" icon={<Person sx={{ color: '#3b82f6', fontSize: 20 }} />}>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Phone', value: patient.phone },
                      { label: 'Alternate Phone', value: patient.alternatePhone },
                      { label: 'Email', value: patient.email },
                      { label: 'Street', value: patient.address?.street },
                      { label: 'City', value: patient.address?.city },
                      { label: 'State', value: patient.address?.state },
                      { label: 'Pincode', value: patient.address?.pincode },
                    ].map(({ label, value }) => (
                      <Grid item xs={6} key={label}><InfoItem label={label} value={value} /></Grid>
                    ))}
                  </Grid>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                {/* Vitals */}
                <SectionCard title="Vitals" icon={<Favorite sx={{ color: '#ef4444', fontSize: 20 }} />}
                  action={<Button size="small" startIcon={<Edit />} onClick={() => openDialog('vitals', { height: patient.vitals?.height || '', weight: patient.vitals?.weight || '', bloodPressure: patient.vitals?.bloodPressure || '', pulse: patient.vitals?.pulse || '', temperature: patient.vitals?.temperature || '', oxygenSaturation: patient.vitals?.oxygenSaturation || '' })} sx={{ textTransform: 'none', fontSize: 12 }}>Update</Button>}>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Height', value: patient.vitals?.height ? `${patient.vitals.height} cm` : null },
                      { label: 'Weight', value: patient.vitals?.weight ? `${patient.vitals.weight} kg` : null },
                      { label: 'BMI', value: bmi ? `${bmi} kg/m²` : null },
                      { label: 'Blood Pressure', value: patient.vitals?.bloodPressure },
                      { label: 'Pulse', value: patient.vitals?.pulse ? `${patient.vitals.pulse} bpm` : null },
                      { label: 'Temperature', value: patient.vitals?.temperature ? `${patient.vitals.temperature}°F` : null },
                      { label: 'O₂ Saturation', value: patient.vitals?.oxygenSaturation ? `${patient.vitals.oxygenSaturation}%` : null },
                      { label: 'Last Updated', value: patient.vitals?.lastUpdated ? new Date(patient.vitals.lastUpdated).toLocaleDateString() : null },
                    ].map(({ label, value }) => (
                      <Grid item xs={6} key={label}><InfoItem label={label} value={value || undefined} /></Grid>
                    ))}
                  </Grid>
                </SectionCard>

                {/* Emergency Contacts */}
                <SectionCard title="Emergency Contacts" icon={<Warning sx={{ color: '#f59e0b', fontSize: 20 }} />}
                  action={<Button size="small" startIcon={<Add />} onClick={() => openDialog('emergencyContacts', {})} sx={{ textTransform: 'none', fontSize: 12 }}>Add</Button>}>
                  {patient.emergencyContacts?.length === 0 && <Typography variant="body2" sx={{ color: '#94a3b8' }}>No emergency contacts added</Typography>}
                  {patient.emergencyContacts?.map((c: any, i: number) => (
                    <Box key={i} sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, mb: 1, border: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>{c.relation} • {c.phone}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                        {c.isPrimary && <Chip label="Primary" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontSize: 10 }} />}
                        <IconButton size="small" onClick={() => deleteFromArray('emergencyContacts', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                      </Box>
                    </Box>
                  ))}
                </SectionCard>

                {/* Chronic Conditions */}
                <SectionCard title="Chronic Conditions" icon={<HealthAndSafety sx={{ color: '#8b5cf6', fontSize: 20 }} />}
                  action={<Button size="small" startIcon={<Edit />} onClick={() => openDialog('chronicConditions', { value: patient.chronicConditions?.join(', ') || '' })} sx={{ textTransform: 'none', fontSize: 12 }}>Edit</Button>}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {patient.chronicConditions?.length > 0
                      ? patient.chronicConditions.map((c: string) => <Chip key={c} label={c} size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} />)
                      : <Typography variant="body2" sx={{ color: '#94a3b8' }}>None recorded</Typography>}
                  </Box>
                </SectionCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 1: Medical History */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Medical History</Typography>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openDialog('medicalHistory', {})}
                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>Add Condition</Button>
            </Box>
            {patient.medicalHistory?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <History sx={{ fontSize: 48, mb: 1, color: '#e2e8f0' }} />
                <Typography>No medical history recorded</Typography>
              </Box>
            )}
            {patient.medicalHistory?.map((h: any, i: number) => (
              <Card key={i} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>{h.condition}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {h.diagnosedDate ? `Diagnosed: ${new Date(h.diagnosedDate).toLocaleDateString('en-IN')}` : 'Date unknown'}
                        {h.treatedBy ? ` • Dr. ${h.treatedBy}` : ''}
                        {h.hospital ? ` • ${h.hospital}` : ''}
                      </Typography>
                      {h.notes && <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>{h.notes}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label={h.status} size="small" sx={{
                        textTransform: 'capitalize',
                        bgcolor: h.status === 'active' ? '#fef9c3' : h.status === 'resolved' ? '#dcfce7' : h.status === 'chronic' ? '#fee2e2' : '#f1f5f9',
                        color: h.status === 'active' ? '#a16207' : h.status === 'resolved' ? '#15803d' : h.status === 'chronic' ? '#b91c1c' : '#64748b',
                      }} />
                      <IconButton size="small" onClick={() => deleteFromArray('medicalHistory', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}

            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Surgical History</Typography>
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => openDialog('surgicalHistory', {})}
                sx={{ borderRadius: 2, textTransform: 'none' }}>Add Surgery</Button>
            </Box>
            {patient.surgicalHistory?.map((s: any, i: number) => (
              <Card key={i} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 1.5 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{s.procedure}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {s.date ? new Date(s.date).toLocaleDateString('en-IN') : ''}
                      {s.surgeon ? ` • Dr. ${s.surgeon}` : ''}
                      {s.hospital ? ` • ${s.hospital}` : ''}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => deleteFromArray('surgicalHistory', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                </CardContent>
              </Card>
            ))}
            {patient.surgicalHistory?.length === 0 && <Typography variant="body2" sx={{ color: '#94a3b8' }}>No surgical history recorded</Typography>}

            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Current Medications</Typography>
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => openDialog('currentMedications', {})}
                sx={{ borderRadius: 2, textTransform: 'none' }}>Add Medication</Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Medicine', 'Dosage', 'Frequency', 'Prescribed By', 'Status', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#374151' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {patient.currentMedications?.map((m: any, i: number) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{m.name}</TableCell>
                    <TableCell>{m.dosage || '—'}</TableCell>
                    <TableCell>{m.frequency || '—'}</TableCell>
                    <TableCell>{m.prescribedBy || '—'}</TableCell>
                    <TableCell>
                      <Chip label={m.isActive ? 'Active' : 'Stopped'} size="small" sx={{ bgcolor: m.isActive ? '#dcfce7' : '#f1f5f9', color: m.isActive ? '#15803d' : '#64748b', fontSize: 11 }} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => deleteFromArray('currentMedications', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {patient.currentMedications?.length === 0 && <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>No medications recorded</Typography>}
          </TabPanel>

          {/* TAB 2: Family History */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Family Medical History</Typography>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openDialog('familyHistory', {})}
                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>Add</Button>
            </Box>
            {patient.familyHistory?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <FamilyRestroom sx={{ fontSize: 48, mb: 1, color: '#e2e8f0' }} />
                <Typography>No family history recorded</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {patient.familyHistory?.map((f: any, i: number) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                          <Chip label={f.relation} size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', textTransform: 'capitalize', mb: 1 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.condition}</Typography>
                          {f.notes && <Typography variant="caption" sx={{ color: '#64748b' }}>{f.notes}</Typography>}
                        </Box>
                        <IconButton size="small" onClick={() => deleteFromArray('familyHistory', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* TAB 3: Allergies */}
          <TabPanel value={tab} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Allergies</Typography>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openDialog('allergies', {})}
                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>Add Allergy</Button>
            </Box>
            {patient.allergies?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <Warning sx={{ fontSize: 48, mb: 1, color: '#e2e8f0' }} />
                <Typography>No allergies recorded</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {patient.allergies?.map((a: any, i: number) => {
                const severityColor: Record<string, any> = {
                  mild: { bg: '#dcfce7', color: '#15803d' }, moderate: { bg: '#fef9c3', color: '#a16207' },
                  severe: { bg: '#fee2e2', color: '#b91c1c' }, 'life-threatening': { bg: '#fecdd3', color: '#9f1239' }
                };
                const sc = severityColor[a.severity] || severityColor.mild;
                return (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, borderLeft: `4px solid ${sc.color}` }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{a.allergen}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip label={a.type} size="small" variant="outlined" sx={{ fontSize: 10, textTransform: 'capitalize' }} />
                              <Chip label={a.severity} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontSize: 10, textTransform: 'capitalize' }} />
                            </Box>
                            {a.reaction && <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>Reaction: {a.reaction}</Typography>}
                          </Box>
                          <IconButton size="small" onClick={() => deleteFromArray('allergies', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </TabPanel>

          {/* TAB 4: Insurance */}
          <TabPanel value={tab} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Insurance Details</Typography>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openDialog('insurance', {})}
                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>Add Insurance</Button>
            </Box>
            {patient.insurance?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <HealthAndSafety sx={{ fontSize: 48, mb: 1, color: '#e2e8f0' }} />
                <Typography>No insurance details added</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {patient.insurance?.map((ins: any, i: number) => {
                const expired = ins.validTill && new Date(ins.validTill) < new Date();
                return (
                  <Grid item xs={12} sm={6} key={i}>
                    <Card elevation={0} sx={{ border: `1px solid ${expired ? '#fecaca' : '#e2e8f0'}`, borderRadius: 2 }}>
                      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>{ins.provider}</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>Policy: {ins.policyNumber}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            {ins.isPrimary && <Chip label="Primary" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontSize: 10 }} />}
                            <Chip label={expired ? 'Expired' : 'Active'} size="small" sx={{ bgcolor: expired ? '#fee2e2' : '#dcfce7', color: expired ? '#b91c1c' : '#15803d', fontSize: 10 }} />
                            <IconButton size="small" onClick={() => deleteFromArray('insurance', i)} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                          </Box>
                        </Box>
                        <Grid container spacing={1}>
                          {[
                            { label: 'Holder', value: ins.holderName },
                            { label: 'Relation', value: ins.relation },
                            { label: 'Coverage', value: ins.coverageType },
                            { label: 'Max Coverage', value: ins.maxCoverage ? `₹${ins.maxCoverage.toLocaleString()}` : null },
                            { label: 'Valid From', value: ins.validFrom ? new Date(ins.validFrom).toLocaleDateString('en-IN') : null },
                            { label: 'Valid Till', value: ins.validTill ? new Date(ins.validTill).toLocaleDateString('en-IN') : null },
                          ].filter(x => x.value).map(({ label, value }) => (
                            <Grid item xs={6} key={label}><InfoItem label={label} value={value || undefined} /></Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </TabPanel>

          {/* TAB 5: Documents */}
          <TabPanel value={tab} index={5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Documents</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                <Button variant="contained" size="small" startIcon={uploading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Upload />}
                  onClick={() => fileRef.current?.click()} disabled={uploading}
                  sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </Box>
            </Box>
            {patient.documents?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <Description sx={{ fontSize: 48, mb: 1, color: '#e2e8f0' }} />
                <Typography>No documents uploaded</Typography>
                <Typography variant="caption">Upload lab reports, prescriptions, ID proof, etc.</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {patient.documents?.map((doc: any, i: number) => {
                const typeColors: Record<string, string> = { lab_report: '#dbeafe', prescription: '#dcfce7', insurance: '#f3e8ff', id_proof: '#fef9c3', xray: '#f1f5f9', other: '#f1f5f9' };
                const isPdf = doc.mimeType?.includes('pdf') || doc.fileName?.endsWith('.pdf');
                const isImage = doc.mimeType?.includes('image');
                return (
                  <Grid item xs={12} sm={6} md={4} key={doc._id || i}>
                    <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }, transition: 'box-shadow 0.2s' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }} noWrap>{doc.name}</Typography>
                            </Box>
                            <Chip label={doc.type?.replace('_', ' ')} size="small" sx={{ bgcolor: typeColors[doc.type] || '#f1f5f9', fontSize: 10, textTransform: 'capitalize', mb: 0.5 }} />
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                              {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(0)} KB` : ''} • {new Date(doc.uploadedAt).toLocaleDateString('en-IN')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Tooltip title="Download">
                              <IconButton size="small" component="a" href={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${doc.filePath}`} target="_blank" sx={{ color: '#3b82f6' }}>
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => deleteDocument(doc._id)} sx={{ color: '#ef4444' }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </TabPanel>
        </Box>
      </Card>

      {/* Universal Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
          {dialogType === 'basic' ? 'Edit Personal Info' :
           dialogType === 'vitals' ? 'Update Vitals' :
           dialogType === 'emergencyContacts' ? 'Add Emergency Contact' :
           dialogType === 'medicalHistory' ? 'Add Medical Condition' :
           dialogType === 'surgicalHistory' ? 'Add Surgery' :
           dialogType === 'familyHistory' ? 'Add Family History' :
           dialogType === 'allergies' ? 'Add Allergy' :
           dialogType === 'insurance' ? 'Add Insurance' :
           dialogType === 'currentMedications' ? 'Add Medication' :
           dialogType === 'chronicConditions' ? 'Edit Chronic Conditions' : 'Edit'}
          <IconButton size="small" onClick={() => setEditOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <DialogFields type={dialogType} data={dialogData} onChange={setDialogData} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" disabled={saving}
            onClick={() => saveSection(dialogType, dialogData)}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Dialog fields per type
const DialogFields = ({ type, data, onChange }: any) => {
  const set = (k: string, v: any) => onChange((d: any) => ({ ...d, [k]: v }));

  const F = ({ label, k, type: t = 'text', children }: any) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>{label}</Typography>
      {children || <TextField fullWidth size="small" type={t} value={data[k] || ''} onChange={e => set(k, e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />}
    </Grid>
  );

  if (type === 'basic') return (
    <Grid container spacing={2}>
      <F label="Full Name" k="name" /><F label="Phone" k="phone" /><F label="Email" k="email" />
      <F label="Date of Birth" k="dob" t="date" /><F label="Occupation" k="occupation" />
      <F label="Gender" k="gender">
        <TextField select fullWidth size="small" value={data.gender || ''} onChange={e => set('gender', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['male','female','other'].map(g => <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g.charAt(0).toUpperCase()+g.slice(1)}</MenuItem>)}
        </TextField>
      </F>
      <F label="Blood Group" k="bloodGroup">
        <TextField select fullWidth size="small" value={data.bloodGroup || ''} onChange={e => set('bloodGroup', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['A+','A-','B+','B-','AB+','AB-','O+','O-','Unknown'].map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>
      </F>
    </Grid>
  );

  if (type === 'vitals') return (
    <Grid container spacing={2}>
      <F label="Height (cm)" k="height" t="number" /><F label="Weight (kg)" k="weight" t="number" />
      <F label="Blood Pressure" k="bloodPressure" /><F label="Pulse (bpm)" k="pulse" t="number" />
      <F label="Temperature (°F)" k="temperature" t="number" /><F label="O₂ Saturation (%)" k="oxygenSaturation" t="number" />
    </Grid>
  );

  if (type === 'emergencyContacts') return (
    <Grid container spacing={2}>
      <F label="Full Name *" k="name" /><F label="Phone *" k="phone" />
      <F label="Relation" k="relation" /><F label="Alternate Phone" k="alternatePhone" />
      <F label="Email" k="email" /><F label="Address" k="address" />
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input type="checkbox" checked={data.isPrimary || false} onChange={e => set('isPrimary', e.target.checked)} />
          <Typography variant="body2">Set as primary contact</Typography>
        </Box>
      </Grid>
    </Grid>
  );

  if (type === 'medicalHistory') return (
    <Grid container spacing={2}>
      <F label="Condition *" k="condition" /><F label="Diagnosed Date" k="diagnosedDate" t="date" />
      <F label="Treated By (Doctor)" k="treatedBy" /><F label="Hospital" k="hospital" />
      <F label="Status" k="status">
        <TextField select fullWidth size="small" value={data.status || 'active'} onChange={e => set('status', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['active','resolved','chronic','unknown'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
        </TextField>
      </F>
      <Grid item xs={12}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Notes</Typography>
        <TextField fullWidth size="small" multiline rows={2} value={data.notes || ''} onChange={e => set('notes', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
      </Grid>
    </Grid>
  );

  if (type === 'surgicalHistory') return (
    <Grid container spacing={2}>
      <F label="Procedure *" k="procedure" /><F label="Date" k="date" t="date" />
      <F label="Surgeon" k="surgeon" /><F label="Hospital" k="hospital" />
      <Grid item xs={12}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Notes</Typography>
        <TextField fullWidth size="small" multiline rows={2} value={data.notes || ''} onChange={e => set('notes', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
      </Grid>
    </Grid>
  );

  if (type === 'familyHistory') return (
    <Grid container spacing={2}>
      <F label="Relation" k="relation">
        <TextField select fullWidth size="small" value={data.relation || ''} onChange={e => set('relation', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['father','mother','sibling','grandparent','child','other'].map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.charAt(0).toUpperCase()+r.slice(1)}</MenuItem>)}
        </TextField>
      </F>
      <F label="Condition *" k="condition" />
      <Grid item xs={12}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Notes</Typography>
        <TextField fullWidth size="small" multiline rows={2} value={data.notes || ''} onChange={e => set('notes', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
      </Grid>
    </Grid>
  );

  if (type === 'allergies') return (
    <Grid container spacing={2}>
      <F label="Allergen *" k="allergen" />
      <F label="Type" k="type">
        <TextField select fullWidth size="small" value={data.type || 'other'} onChange={e => set('type', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['drug','food','environmental','other'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase()+t.slice(1)}</MenuItem>)}
        </TextField>
      </F>
      <F label="Severity" k="severity">
        <TextField select fullWidth size="small" value={data.severity || 'mild'} onChange={e => set('severity', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['mild','moderate','severe','life-threatening'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
        </TextField>
      </F>
      <F label="Reaction" k="reaction" /><F label="Diagnosed Date" k="diagnosedDate" t="date" />
    </Grid>
  );

  if (type === 'insurance') return (
    <Grid container spacing={2}>
      <F label="Insurance Provider *" k="provider" /><F label="Policy Number *" k="policyNumber" />
      <F label="Group Number" k="groupNumber" /><F label="Policy Holder Name" k="holderName" />
      <F label="Relation to Patient" k="relation" /><F label="Valid From" k="validFrom" t="date" />
      <F label="Valid Till" k="validTill" t="date" /><F label="Max Coverage (₹)" k="maxCoverage" t="number" />
      <F label="Coverage Type" k="coverageType">
        <TextField select fullWidth size="small" value={data.coverageType || ''} onChange={e => set('coverageType', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
          {['individual','family','group'].map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase()+t.slice(1)}</MenuItem>)}
        </TextField>
      </F>
      <F label="Contact Number" k="contactNumber" />
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input type="checkbox" checked={data.isPrimary || false} onChange={e => set('isPrimary', e.target.checked)} />
          <Typography variant="body2">Set as primary insurance</Typography>
        </Box>
      </Grid>
    </Grid>
  );

  if (type === 'currentMedications') return (
    <Grid container spacing={2}>
      <F label="Medicine Name *" k="name" /><F label="Dosage" k="dosage" />
      <F label="Frequency" k="frequency" /><F label="Prescribed By" k="prescribedBy" />
      <F label="Start Date" k="startDate" t="date" /><F label="End Date" k="endDate" t="date" />
    </Grid>
  );

  if (type === 'chronicConditions') return (
    <Grid item xs={12} component={Grid} container>
      <Grid item xs={12}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Conditions (comma separated)</Typography>
        <TextField fullWidth size="small" multiline rows={3} value={data.value || ''} onChange={e => set('value', e.target.value)}
          placeholder="e.g. Diabetes, Hypertension, Asthma"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
      </Grid>
    </Grid>
  );

  return null;
};

export default PatientProfile;
